import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Play, Pause, Maximize, Volume2, VolumeX, X, Edit3, MoreHorizontal, Pin, Trash2 } from 'lucide-react';
import WorkEditModal from '../Profile/work/WorkEditModal';
import { Modal } from './Modal';
import { CATEGORY_TAGS, TOOL_TAGS, STYLE_TAGS, TOPIC_TAGS } from '../../constants/tags';
import { useNavigate } from 'react-router-dom';
import { FollowButton } from './FollowButton';
import { useToast } from './Toast';
import { postService, type WorkCollaborator } from '../../services/postService';
import CommentThreadSection from './CommentThreadSection';
import { buildShareClipboardText, buildWorkShareText, buildWorkShareUrl, copyTextToClipboard } from '../../utils/share';

export interface WorkComment {
    id: string | number;
    authorId?: string;
    avatar: string;
    username: string;
    content: string;
    time: string;
    location: string;
    likes: number;
    liked?: boolean;
    replies?: WorkComment[];
}

// 作品详情类型
export interface WorkDetail {
    id: string | number;
    type: 'video' | 'image';
    videoUrl?: string;
    images?: string[];
    thumbnailUrl: string;
    title: string;
    description: string;
    sourceUrl?: string;
    tags: string[];
    likes: number;
    views: number;
    favorites: number;
    comments: WorkComment[];
    commentCount?: number;
    commentThreadId?: string;
    createdAt: string;
    pinnedAt?: number;
    author: {
        id: string;
        name: string;
        avatar: string;
    };
    collaborators?: WorkCollaborator[];
    isPinned?: boolean;
    liked?: boolean;
    bookmarked?: boolean;
    auditStatus?: 'WAITING_UPLOAD' | 'PENDING' | 'REJECTED' | 'APPROVED';
    visibility?: 'public' | 'private' | 'unlisted' | 'draft';
    status?: 'active' | 'archived' | 'pending_upload' | 'under_review' | 'rejected' | 'deleted';
}

interface VideoModalProps {
    work: WorkDetail;
    isOpen: boolean;
    onClose: () => void;
    isOwnProfile?: boolean;
    enablePostInteractions?: boolean;
    onTogglePin?: (id: string | number) => void;
    pinDisabledReason?: string;
    onDelete?: (id: string | number) => void | Promise<void>;
    zIndex?: number;
}

const CollaboratorAvatarButton: React.FC<{
    collaborator: WorkCollaborator;
    onClick: () => void;
}> = ({ collaborator, onClick }) => {
    const [hasAvatarError, setHasAvatarError] = useState(false);
    const initial = (collaborator.name || collaborator.username || 'U').slice(0, 1).toUpperCase();

    return (
        <button
            type="button"
            onClick={onClick}
            className="h-9 w-9 shrink-0 overflow-hidden rounded-full bg-gray-100 ring-1 ring-gray-200 transition hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-blue-400"
            aria-label={`查看 ${collaborator.name || collaborator.username || collaborator.id} 的主页`}
        >
            {collaborator.avatar && !hasAvatarError ? (
                <img
                    src={collaborator.avatar}
                    alt={collaborator.name || collaborator.username || collaborator.id}
                    className="h-full w-full object-cover"
                    onError={() => setHasAvatarError(true)}
                />
            ) : (
                <span className="flex h-full w-full items-center justify-center text-sm font-semibold text-gray-600">
                    {initial}
                </span>
            )}
        </button>
    );
};

const VideoModal: React.FC<VideoModalProps> = ({
    work,
    isOpen,
    onClose,
    isOwnProfile = true,
    enablePostInteractions,
    onTogglePin,
    pinDisabledReason,
    onDelete,
    zIndex = 70
}) => {
    const navigate = useNavigate();
    const videoRef = useRef<HTMLVideoElement>(null);
    const isClosingRef = useRef(false);
    const shareResetTimerRef = useRef<number | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [volume, setVolume] = useState(1);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isLiked, setIsLiked] = useState(!!work.liked);
    const [isFavorited, setIsFavorited] = useState(!!work.bookmarked);
    const [likeCount, setLikeCount] = useState(work.likes);
    const [favoriteCount, setFavoriteCount] = useState(work.favorites);
    const [comments, setComments] = useState(work.comments);
    const [commentCount, setCommentCount] = useState(work.commentCount ?? work.comments.length);
    const [isTogglingLike, setIsTogglingLike] = useState(false);
    const [isTogglingBookmark, setIsTogglingBookmark] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isShareCopied, setIsShareCopied] = useState(false);
    const [hasAuthorAvatarError, setHasAuthorAvatarError] = useState(false);
    const [mediaAspectRatio, setMediaAspectRatio] = useState<number | null>(null);
    const { showToast } = useToast();

    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const canUsePostInteractions = enablePostInteractions ?? (
        typeof work.id === 'string' && work.id.startsWith('post_')
    );
    const postId = canUsePostInteractions ? String(work.id) : null;
    const commentThreadId = work.commentThreadId || postId;
    const shareUrl = buildWorkShareUrl(work.author.id, work.id);
    const shareText = buildWorkShareText(work.title, work.author.name);
    const pinActionBlocked = Boolean(pinDisabledReason && !work.isPinned);
    const collaborators = useMemo(
        () => (work.collaborators || []).filter((collaborator) => collaborator.id),
        [work.collaborators]
    );
    // Track whether we should render the mobile (full-screen, single
    // column) variant of the modal. Re-checked on resize so rotating
    // the device or resizing the browser window swaps layouts cleanly.
    const [isMobileViewport, setIsMobileViewport] = useState(() => {
        if (typeof window === 'undefined') return false;
        return window.matchMedia('(max-width: 767px)').matches;
    });
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const mql = window.matchMedia('(max-width: 767px)');
        const handler = (e: MediaQueryListEvent) => setIsMobileViewport(e.matches);
        // Some Safari versions still need addListener; use the modern API where available.
        if (mql.addEventListener) mql.addEventListener('change', handler);
        else mql.addListener(handler);
        return () => {
            if (mql.removeEventListener) mql.removeEventListener('change', handler);
            else mql.removeListener(handler);
        };
    }, []);

    const isLandscapeLayout = useMemo(() => {
        // On mobile we always stack vertically (media on top, details below)
        // regardless of aspect ratio — phones are too narrow for a side-by-side
        // media + details split.
        if (isMobileViewport) return true;
        if (mediaAspectRatio == null) {
            return true; // Default to horizontal while loading to avoid jumping
        }
        return mediaAspectRatio >= 1;
    }, [mediaAspectRatio, isMobileViewport]);

    const fixedLayoutMetrics = useMemo(() => {
        if (isMobileViewport) {
            // Mobile: modal flows like a regular scrolling page. Modal
            // is `overflow-y-auto` (set in className below); media pane
            // is fixed at 55vh; details pane has NO fixed height so
            // tall posts (long descriptions, many comments) extend the
            // modal naturally and the user scrolls the whole thing to
            // reach the comment input.
            return {
                modalStyle: { width: '100vw', minHeight: '100vh', borderRadius: 0 },
                mediaPaneStyle: { width: '100vw', height: '55vh' },
                detailsPaneStyle: { width: '100vw' }
            };
        }
        if (isLandscapeLayout) {
            // Desktop, landscape media: media on top, details (with
            // 2-column inner split) at the bottom.
            return {
                modalStyle: { width: 800, height: 800 },
                mediaPaneStyle: { width: 800, height: 450 },
                detailsPaneStyle: { width: 800, height: 350 }
            };
        }
        // Desktop, portrait media: media on left, details (stacked
        // inner) on right.
        return {
            modalStyle: { width: 800, height: 800 },
            mediaPaneStyle: { width: 450, height: 800 },
            detailsPaneStyle: { width: 350, height: 800 }
        };
    }, [isLandscapeLayout, isMobileViewport]);

    useEffect(() => {
        const currentVideo = videoRef.current;

        if (isOpen) {
            isClosingRef.current = false;
            document.body.style.overflow = 'hidden';
            setCurrentImageIndex(0);
            if (postId) {
                void postService.trackView(postId);
            }
        } else {
            isClosingRef.current = true;
            if (currentVideo) {
                currentVideo.pause();
            }
            setIsPlaying(false);
            document.body.style.overflow = 'unset';
        }

        // Always restore body overflow on unmount. The parent (Home /
        // Profile) renders this modal as `{selectedWork && <Modal/>}`,
        // so closing unmounts immediately — without an unconditional
        // cleanup, the open-branch leaks `overflow: hidden` and locks
        // the page scroll until full reload.
        return () => {
            isClosingRef.current = true;
            if (currentVideo) {
                currentVideo.pause();
            }
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, postId]);

    useEffect(() => {
        if (!isOpen) {
            setMediaAspectRatio(null);
            return;
        }

        let isMounted = true;

        // Seed videos with a default aspect so the modal can lay out before
        // `loadedmetadata` arrives. iOS WeChat in-app browsers refuse to
        // preload video data until the user taps somewhere — without this
        // seed, the spinner overlay sat forever and users had to tap a
        // blank area to "wake up" the load. Real aspect overrides this
        // once metadata fires.
        if (work.type === 'video') {
            setMediaAspectRatio(prev => prev ?? 16 / 9);
        }

        if (work.type === 'image') {
            const imageSource = work.images?.[currentImageIndex] || work.thumbnailUrl;

            if (!imageSource) {
                setMediaAspectRatio(null);
                return () => {
                    isMounted = false;
                };
            }

            const image = new Image();
            image.onload = () => {
                if (!isMounted || !image.naturalWidth || !image.naturalHeight) {
                    return;
                }

                setMediaAspectRatio(image.naturalWidth / image.naturalHeight);
            };
            image.onerror = () => {
                if (isMounted) {
                    setMediaAspectRatio(null);
                }
            };
            image.src = imageSource;

            return () => {
                isMounted = false;
            };
        }

        const video = videoRef.current;
        if (!video) {
            return () => {
                isMounted = false;
            };
        }

        const updateVideoAspectRatio = () => {
            if (!isMounted || !video.videoWidth || !video.videoHeight) {
                return;
            }

            setMediaAspectRatio(video.videoWidth / video.videoHeight);
        };

        updateVideoAspectRatio();
        video.addEventListener('loadedmetadata', updateVideoAspectRatio);

        return () => {
            isMounted = false;
            video.removeEventListener('loadedmetadata', updateVideoAspectRatio);
        };
    }, [currentImageIndex, isOpen, work.images, work.thumbnailUrl, work.type, work.videoUrl]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setIsLiked(!!work.liked);
        setIsFavorited(!!work.bookmarked);
        setLikeCount(work.likes);
        setFavoriteCount(work.favorites);
        setComments(work.comments);
        setCommentCount(work.commentCount ?? work.comments.length);
    }, [isOpen, work.likes, work.favorites, work.liked, work.bookmarked, work.comments, work.commentCount]);

    const handleCommentsChange = useCallback((nextComments: WorkComment[], nextCommentCount: number) => {
        setComments(nextComments);
        setCommentCount(nextCommentCount);
    }, []);

    const handleShare = useCallback(async () => {
        try {
            await copyTextToClipboard(buildShareClipboardText(shareText, shareUrl));
            setIsShareCopied(true);
            if (shareResetTimerRef.current) {
                window.clearTimeout(shareResetTimerRef.current);
            }
            shareResetTimerRef.current = window.setTimeout(() => {
                setIsShareCopied(false);
            }, 1800);
            showToast('分享信息已复制', 'success');
        } catch (error) {
            console.error('Copy work share content failed:', error);
            showToast('分享信息复制失败', 'error');
        }
    }, [shareText, shareUrl, showToast]);

    useEffect(() => {
        setHasAuthorAvatarError(false);
    }, [work.author.avatar]);

    useEffect(() => {
        return () => {
            if (shareResetTimerRef.current) {
                window.clearTimeout(shareResetTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || work.type !== 'video') return;

        const updateTime = () => setCurrentTime(video.currentTime);
        const updateDuration = () => setDuration(video.duration);
        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);
        const handleEnded = () => setIsPlaying(false);

        video.addEventListener('timeupdate', updateTime);
        video.addEventListener('loadedmetadata', updateDuration);
        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);
        video.addEventListener('ended', handleEnded);

        return () => {
            video.removeEventListener('timeupdate', updateTime);
            video.removeEventListener('loadedmetadata', updateDuration);
            video.removeEventListener('play', handlePlay);
            video.removeEventListener('pause', handlePause);
            video.removeEventListener('ended', handleEnded);
        };
    }, [work.type]);

    const handleClose = () => {
        isClosingRef.current = true;
        if (videoRef.current) {
            videoRef.current.pause();
        }
        setIsPlaying(false);
        onClose();
    };

    const togglePlay = () => {
        if (work.type !== 'video' || !videoRef.current) return;

        if (isPlaying) {
            videoRef.current.pause();
        } else {
            const playPromise = videoRef.current.play();
            if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch((error: any) => {
                    if (
                        isClosingRef.current ||
                        error?.name === 'AbortError' ||
                        String(error?.message || '').includes('interrupted')
                    ) {
                        return;
                    }

                    console.error('Failed to play video:', error);
                });
            }
        }
    };

    const toggleMute = () => {
        if (videoRef.current) {
            const newMuted = !isMuted;
            videoRef.current.muted = newMuted;
            setIsMuted(newMuted);
            if (newMuted) {
                setVolume(0);
            } else {
                setVolume(1);
                videoRef.current.volume = 1;
            }
        }
    };

    const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newVolume = parseFloat(e.target.value);
        if (videoRef.current) {
            videoRef.current.volume = newVolume;
            setVolume(newVolume);
            setIsMuted(newVolume === 0);
        }
    };

    const handleFullscreen = () => {
        if (videoRef.current) {
            if (videoRef.current.requestFullscreen) {
                videoRef.current.requestFullscreen();
            } else if ((videoRef.current as any).webkitRequestFullscreen) {
                (videoRef.current as any).webkitRequestFullscreen();
            }
        }
    };

    const handleLikeToggle = async () => {
        if (!postId || isTogglingLike) {
            return;
        }

        const previousLiked = isLiked;
        const previousLikeCount = likeCount;
        const optimisticLiked = !previousLiked;
        const optimisticLikeCount = Math.max(0, previousLikeCount + (optimisticLiked ? 1 : -1));

        setIsTogglingLike(true);
        setIsLiked(optimisticLiked);
        setLikeCount(optimisticLikeCount);
        work.liked = optimisticLiked;
        work.likes = optimisticLikeCount;

        const response = await postService.toggleLike(postId);
        setIsTogglingLike(false);

        if (!response.success) {
            setIsLiked(previousLiked);
            setLikeCount(previousLikeCount);
            work.liked = previousLiked;
            work.likes = previousLikeCount;
            showToast(response.error || "点赞失败", "error");
            return;
        }

        const nextLiked = response.data?.liked ?? optimisticLiked;
        const reconciledLikeCount = Math.max(0, previousLikeCount + (nextLiked ? 1 : -1));
        setIsLiked(nextLiked);
        setLikeCount(reconciledLikeCount);
        work.liked = nextLiked;
        work.likes = reconciledLikeCount;
        window.dispatchEvent(new Event('interactions_updated'));
    };

    const handleBookmarkToggle = async () => {
        if (!postId || isTogglingBookmark) {
            return;
        }

        const previousBookmarked = isFavorited;
        const previousFavoriteCount = favoriteCount;
        const optimisticBookmarked = !previousBookmarked;
        const optimisticFavoriteCount = Math.max(0, previousFavoriteCount + (optimisticBookmarked ? 1 : -1));

        setIsTogglingBookmark(true);
        setIsFavorited(optimisticBookmarked);
        setFavoriteCount(optimisticFavoriteCount);
        work.bookmarked = optimisticBookmarked;
        work.favorites = optimisticFavoriteCount;

        const response = await postService.toggleBookmark(postId);
        setIsTogglingBookmark(false);

        if (!response.success) {
            setIsFavorited(previousBookmarked);
            setFavoriteCount(previousFavoriteCount);
            work.bookmarked = previousBookmarked;
            work.favorites = previousFavoriteCount;
            showToast(response.error || "收藏失败", "error");
            return;
        }

        const nextBookmarked = response.data?.bookmarked ?? optimisticBookmarked;
        const reconciledFavoriteCount = Math.max(0, previousFavoriteCount + (nextBookmarked ? 1 : -1));
        setIsFavorited(nextBookmarked);
        setFavoriteCount(reconciledFavoriteCount);
        work.bookmarked = nextBookmarked;
        work.favorites = reconciledFavoriteCount;
        window.dispatchEvent(new Event('interactions_updated'));
    };

    const confirmDelete = () => {
        Promise.resolve(onDelete?.(work.id))
            .then(() => {
                setIsDeleteModalOpen(false);
                onClose();
            })
            .catch(() => undefined);
    };

    const formatTime = (time: number) => {
        const mins = Math.floor(time / 60);
        const secs = Math.floor(time % 60);
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
        const bar = e.currentTarget;
        const rect = bar.getBoundingClientRect();
        const percent = (e.clientX - rect.left) / rect.width;
        if (videoRef.current && duration) {
            videoRef.current.currentTime = percent * duration;
        }
    };

    // Image navigation
    const nextImage = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        if (work.images && work.images.length > 1) {
            setCurrentImageIndex((prev) => (prev + 1) % work.images!.length);
        }
    };

    const prevImage = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        if (work.images && work.images.length > 1) {
            setCurrentImageIndex((prev) => (prev - 1 + work.images!.length) % work.images!.length);
        }
    };

    if (!isOpen) return null;

    // Two independent layout decisions:
    //   - modalIsColumn: outer flex direction. Column = media on top,
    //     details below. Row = media left, details right.
    //   - detailsIsTwoColumn: should the desktop landscape variant put
    //     author info next to the comments column? Only true on
    //     desktop landscape media (the wide bottom strip has room).
    const modalIsColumn = isLandscapeLayout || isMobileViewport;
    const detailsIsTwoColumn = isLandscapeLayout && !isMobileViewport;

    const modalContainerClassName = isMobileViewport
        // Mobile: modal grows naturally. The OUTER wrapper (fixed inset-0)
        // is the scroll container; the modal itself just lays out children
        // top-to-bottom and lets them extend past 100vh as needed.
        ? 'relative flex flex-col bg-white shadow-2xl'
        : modalIsColumn
            // Desktop landscape: column flex, fixed 800x800.
            ? 'relative flex min-h-0 flex-col overflow-hidden rounded-2xl bg-white shadow-2xl'
            // Desktop portrait: row flex, fixed 800x800.
            : 'relative flex min-h-0 overflow-hidden rounded-2xl bg-white shadow-2xl';
    const mediaPaneClassName = modalIsColumn
        ? 'relative flex flex-none flex-col bg-black group'
        : 'relative flex min-h-0 flex-none flex-col bg-black group';
    const detailsPaneClassName = isMobileViewport
        // Mobile: details flow naturally below media as part of page scroll.
        // No min-h-0 / overflow-hidden so content can extend the modal.
        ? 'flex flex-none flex-col bg-white'
        : detailsIsTwoColumn
            // Desktop landscape: 2-column row inside.
            ? 'flex min-h-0 flex-1 bg-white'
            // Desktop portrait: stacked column inside.
            : 'flex min-h-0 flex-none flex-col overflow-hidden bg-white';
    const mediaContent = work.type === 'video' ? (
        <>
            <div className="flex-1 min-h-0 relative overflow-hidden">
                <video
                    ref={videoRef}
                    src={work.videoUrl}
                    className="h-full w-full bg-black object-contain"
                    preload="auto"
                    playsInline
                    onClick={togglePlay}
                />

                {!isPlaying && (
                    <button
                        onClick={togglePlay}
                        className="absolute inset-0 flex items-center justify-center bg-black/20"
                    >
                        <div className="w-20 h-20 bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/40 transition-all">
                            <Play className="w-8 h-8 text-white ml-1" fill="white" />
                        </div>
                    </button>
                )}

                <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 px-4 pb-4 pt-16 bg-gradient-to-t from-black/75 via-black/20 to-transparent">
                    <div
                        className="pointer-events-auto mb-3 h-1 bg-white/30 rounded-full cursor-pointer group/seek"
                        onClick={handleSeek}
                    >
                        <div
                            className="h-full bg-white rounded-full relative"
                            style={{ width: duration ? `${(currentTime / duration) * 100}%` : '0%' }}
                        >
                            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover/seek:opacity-100 transition-opacity" />
                        </div>
                    </div>

                    <div className="pointer-events-auto flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 rounded-full bg-black/35 px-3 py-2 backdrop-blur-sm">
                            <button onClick={togglePlay} className="text-white hover:text-gray-300 transition-colors">
                                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                            </button>
                            <span className="text-white text-sm font-mono">
                                {formatTime(currentTime)}/{formatTime(duration)}
                            </span>
                        </div>

                        <div className="flex items-center gap-3 rounded-full bg-black/35 px-3 py-2 backdrop-blur-sm">
                            <div className="flex items-center gap-2 group/volume">
                                <button onClick={toggleMute} className="text-white hover:text-gray-300 transition-colors">
                                    {isMuted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                                </button>
                                <input
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.05"
                                    value={isMuted ? 0 : volume}
                                    onChange={handleVolumeChange}
                                    className="w-0 overflow-hidden group-hover/volume:w-20 transition-all duration-300 h-1 bg-white/30 rounded-lg appearance-none cursor-pointer accent-white"
                                />
                            </div>

                            <button onClick={handleFullscreen} className="text-white hover:text-gray-300 transition-colors">
                                <Maximize className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    ) : (
        <div className="flex-1 relative flex items-center justify-center bg-black w-full h-full">
            {work.images && work.images.length > 0 ? (
                <>
                    <img
                        src={work.images[currentImageIndex]}
                        alt={`Slide ${currentImageIndex + 1}`}
                        className="max-w-full max-h-full object-contain"
                    />

                    {work.images.length > 1 && (
                        <>
                            <button
                                onClick={prevImage}
                                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/30 hover:bg-black/50 text-white flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
                            >
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <button
                                onClick={nextImage}
                                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/30 hover:bg-black/50 text-white flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
                            >
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>

                            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
                                {work.images.map((_, idx) => (
                                    <div
                                        key={idx}
                                        className={`w-1.5 h-1.5 rounded-full transition-all ${idx === currentImageIndex ? 'bg-white w-3' : 'bg-white/40'}`}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </>
            ) : (
                <img
                    src={work.thumbnailUrl}
                    alt={work.title}
                    className="max-w-full max-h-full object-contain"
                />
            )}
        </div>
    );
    const headerActions = isOwnProfile && canUsePostInteractions ? (
        <>
            <button
                onClick={() => {
                    if (pinActionBlocked) {
                        showToast(pinDisabledReason!, 'info');
                        return;
                    }
                    onTogglePin?.(work.id);
                }}
                className={`p-2 rounded-full transition-colors ${
                    pinActionBlocked
                        ? 'text-gray-300 cursor-not-allowed'
                        : work.isPinned
                            ? 'text-blue-500 bg-blue-50 hover:bg-blue-100'
                            : 'text-gray-500 hover:bg-gray-100'
                }`}
                title={pinActionBlocked ? pinDisabledReason : (work.isPinned ? "取消置顶" : "置顶作品")}
            >
                <Pin className={`w-5 h-5 ${work.isPinned ? 'fill-current' : ''}`} />
            </button>
            <button
                onClick={() => setIsEditModalOpen(true)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500 hover:text-gray-700"
                title="编辑作品"
            >
                <Edit3 className="w-5 h-5" />
            </button>
            <button
                onClick={() => setIsDeleteModalOpen(true)}
                className="p-2 hover:bg-red-50 text-gray-500 hover:text-red-500 rounded-full transition-colors"
                title="删除作品"
            >
                <Trash2 className="w-5 h-5" />
            </button>
        </>
    ) : !isOwnProfile ? (
        <>
            <FollowButton userId={work.author.id} />
            <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                <MoreHorizontal className="w-5 h-5 text-gray-600" />
            </button>
        </>
    ) : null;
    const authorHeader = (
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
                <span
                    className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => { onClose(); navigate(`/profile/${work.author.id}`); }}
                >
                {work.author.avatar && !hasAuthorAvatarError ? (
                    <img
                        src={work.author.avatar}
                        alt={work.author.name}
                        className="w-10 h-10 rounded-full object-cover"
                        onError={() => setHasAuthorAvatarError(true)}
                    />
                ) : (
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 text-sm font-semibold text-gray-600">
                        {(work.author.name || 'U').slice(0, 1).toUpperCase()}
                    </div>
                )}
                <div>
                    <h4 className="font-medium text-gray-900">{work.author.name}</h4>
                    <p className="text-xs text-gray-500">{work.createdAt}</p>
                </div>
                </span>
            </div>
            <div className="flex items-center gap-2">
                {headerActions}
            </div>
        </div>
    );
    const detailsSummary = (
        <div className="p-4 border-b border-gray-100">
            <div className="mb-2 flex items-center gap-2">
                <h2 className="text-lg font-semibold text-gray-900">{work.title}</h2>
                {work.visibility === 'draft' && (
                    <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-500">
                        草稿
                    </span>
                )}
            </div>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">{work.description}</p>

            {collaborators.length > 0 && (
                <div className="mb-4">
                    <h5 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2 px-0.5">
                        关联创作者
                    </h5>
                    <div className="space-y-2">
                        {collaborators.map((collaborator) => (
                            <div key={collaborator.id} className="flex min-w-0 items-center gap-2.5">
                                <CollaboratorAvatarButton
                                    collaborator={collaborator}
                                    onClick={() => {
                                        onClose();
                                        navigate(`/profile/${collaborator.id}`);
                                    }}
                                />
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-medium text-gray-900">
                                        {collaborator.name || collaborator.username || collaborator.id}
                                    </p>
                                    {(collaborator.username || collaborator.id) && (
                                        <p className="truncate text-xs text-gray-500">
                                            @{collaborator.username || collaborator.id}
                                        </p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {work.sourceUrl && (
                <div className="mb-4 rounded-xl border border-blue-100 bg-blue-50/70 px-3 py-2">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-blue-500 mb-1">参考链接</p>
                    <a
                        href={work.sourceUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-blue-700 break-all hover:underline"
                    >
                        {work.sourceUrl}
                    </a>
                </div>
            )}

            <div className="space-y-4">
                {[
                    { key: 'tags_category', label: '作品类目', options: CATEGORY_TAGS },
                    { key: 'tags_style', label: '风格流派', options: STYLE_TAGS },
                    { key: 'tags_culture', label: '文化/题材', options: TOPIC_TAGS },
                    { key: 'tags_tools', label: '工具/模型', options: TOOL_TAGS }
                ].map(({ key, label: friendlyLabel, options }) => {
                    const isTools = key === 'tags_tools';
                    const colorClass = isTools
                        ? 'bg-purple-50 text-purple-600 border-purple-100'
                        : 'bg-blue-50 text-blue-600 border-blue-100';

                    const categoryOptionNames = options.map((o: any) => o.name);
                    const matchingTags = work.tags.filter(t => categoryOptionNames.includes(t));

                    if (matchingTags.length === 0) return null;

                    return (
                        <div key={key}>
                            <h5 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2 px-0.5">
                                {friendlyLabel}
                            </h5>
                            <div className="flex flex-wrap gap-2">
                                {matchingTags.map(tag => (
                                    <span key={tag} className={`px-2 py-0.5 text-xs font-medium rounded border ${colorClass}`}>
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                    );
                })}

                {(() => {
                    const allKnownTags = [
                        ...CATEGORY_TAGS,
                        ...TOOL_TAGS,
                        ...STYLE_TAGS,
                        ...TOPIC_TAGS
                    ].map(o => o.name) as string[];
                    const otherTags = work.tags.filter(t => !allKnownTags.includes(t));

                    if (otherTags.length === 0) return null;

                    return (
                        <div className="pt-2">
                            <h5 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2 px-0.5">
                                其他标签
                            </h5>
                            <div className="flex flex-wrap gap-2">
                                {otherTags.map((tag, i) => (
                                    <span key={i} className="text-gray-500 text-xs bg-gray-50 px-2 py-0.5 rounded border border-gray-100">
                                        #{tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                    );
                })()}
            </div>
        </div>
    );
    const commentsSection = (
        <CommentThreadSection
            threadId={commentThreadId}
            initialComments={comments}
            initialCommentCount={commentCount}
            commentsEnabled={!!commentThreadId}
            commentPlaceholder="发表评论..."
            threadOwnerUserId={work.author.id}
            onCommentsChange={handleCommentsChange}
            interactionBar={{
                likeCount,
                favoriteCount,
                isLiked,
                isFavorited,
                canToggleLike: !!postId && !isTogglingLike,
                canToggleBookmark: !!postId && !isTogglingBookmark,
                onToggleLike: handleLikeToggle,
                onToggleBookmark: handleBookmarkToggle,
                onShare: handleShare,
                shareCopied: isShareCopied
            }}
        />
    );

    const modalContent = (
        // Outer wrapper: on mobile we make it `overflow-y-auto` and align
        // content to the top, so a tall modal extends past the viewport
        // and the wrapper itself becomes the scroll container. On desktop
        // we keep the original center-aligned, viewport-clipped layout
        // since the modal is fixed at 800x800.
        <div
            className={`fixed inset-0 flex justify-center ${isMobileViewport ? 'items-start overflow-y-auto' : 'items-center'}`}
            style={{ zIndex }}
        >
            {/* 背景遮罩 */}
            <div
                className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                onClick={handleClose}
            />

            {/* 加载指示器 */}
            {mediaAspectRatio === null && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
                    <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                </div>
            )}

            {/* 关闭按钮.  On mobile the wrapper is scrollable so an
                `absolute` button would scroll out of view; switch to
                `fixed` so it stays anchored to the viewport. */}
            <button
                onClick={handleClose}
                className={`${isMobileViewport ? 'fixed' : 'absolute'} top-4 right-4 sm:top-6 sm:right-6 z-30 w-10 h-10 bg-black/50 sm:bg-white/10 hover:bg-black/70 sm:hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors`}
                aria-label="关闭"
            >
                <X className="w-5 h-5 text-white" />
            </button>

            {/* 主内容容器，等待获取到尺寸后再显示 */}
            <div
                className={`${modalContainerClassName} transition-opacity duration-200 ${mediaAspectRatio === null ? 'opacity-0' : 'opacity-100'}`}
                style={fixedLayoutMetrics.modalStyle}
            >
                {detailsIsTwoColumn ? (
                    <>
                        <div className={mediaPaneClassName} style={fixedLayoutMetrics.mediaPaneStyle}>
                            {mediaContent}
                        </div>
                        <div className={detailsPaneClassName} style={fixedLayoutMetrics.detailsPaneStyle}>
                            <div className="flex min-h-0 w-[360px] flex-col border-r border-gray-100 bg-white">
                                {authorHeader}
                                <div className="min-h-0 overflow-y-auto">
                                    {detailsSummary}
                                </div>
                            </div>
                            <div className="flex min-h-0 flex-1 flex-col bg-white">
                                {commentsSection}
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className={mediaPaneClassName} style={fixedLayoutMetrics.mediaPaneStyle}>
                            {mediaContent}
                        </div>
                        <div className={detailsPaneClassName} style={fixedLayoutMetrics.detailsPaneStyle}>
                            {authorHeader}
                            {detailsSummary}
                            {commentsSection}
                        </div>
                    </>
                )}
            </div>

            {/* 编辑弹出框 */}
            {canUsePostInteractions && postId && (
                <WorkEditModal
                    isOpen={isEditModalOpen}
                    onClose={() => setIsEditModalOpen(false)}
                    work={work}
                    zIndex={zIndex + 10}
                    onSave={async (updatedData, submitMode) => {
                        const response = await postService.updateWork(postId, updatedData, submitMode, {
                            background: true,
                            creator: {
                                id: work.author.id,
                                name: work.author.name,
                                avatar: work.author.avatar
                            },
                            existingWork: {
                                id: postId,
                                creatorUserId: work.author.id,
                                type: work.type,
                                videoUrl: work.videoUrl,
                                images: work.images,
                                thumbnailUrl: work.thumbnailUrl,
                                title: work.title,
                                description: work.description,
                                tags: work.tags,
                                likes: likeCount,
                                views: work.views,
                                favorites: favoriteCount,
                                commentsCount: commentCount,
                                createdAt: work.createdAt,
                                isPinned: work.isPinned,
                                liked: isLiked,
                                bookmarked: isFavorited,
                                collaborators: work.collaborators,
                                visibility: work.visibility,
                                status: work.status,
                                auditStatus: work.auditStatus
                            }
                        });
                        if (!response.success) {
                            showToast(response.error || '作品更新失败', 'error');
                            throw new Error(response.error || '作品更新失败');
                        }

                        Object.assign(work, {
                            ...work,
                            type: updatedData.workType,
                            title: updatedData.title,
                            description: updatedData.description,
                            tags: updatedData.tags || [],
                            thumbnailUrl: updatedData.coverPreview || work.thumbnailUrl,
                            collaborators: updatedData.collaborators || work.collaborators || [],
                            visibility: submitMode === 'draft' ? 'draft' : 'public',
                            status: submitMode === 'draft'
                                ? 'archived'
                                : updatedData.workType === 'image'
                                    ? 'active'
                                    : 'under_review',
                            auditStatus: submitMode === 'draft' ? work.auditStatus : 'PENDING'
                        });

                        showToast(submitMode === 'draft' ? '草稿已加入后台保存' : '作品已加入后台发布', 'success');
                        return { postId: work.id };
                    }}
                />
            )}

            {/* 删除确认弹出框 */}
            {canUsePostInteractions && (
                <Modal
                    isOpen={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    title="确认删除"
                    size="sm"
                    zIndex={zIndex + 10}
                    footer={
                        <>
                            <button
                                onClick={() => setIsDeleteModalOpen(false)}
                                className="px-4 py-2 rounded-full text-gray-600 font-medium hover:bg-gray-100 transition-colors"
                            >
                                取消
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="px-6 py-2 rounded-full bg-red-500 text-white font-medium hover:bg-red-600 transition-colors"
                            >
                                确定删除
                            </button>
                        </>
                    }
                >
                    <div className="text-gray-600 leading-relaxed">
                        确定要删除这个作品吗？此操作无法撤销。
                    </div>
                </Modal>
            )}
        </div>
    );

    return createPortal(modalContent, document.body);
};


export default VideoModal;
